/**
 * Utility functions for parsing and handling links in documentation text
 */

export interface TextSegment {
  type: 'text' | 'link';
  content: string;
  url?: string;
  external?: boolean;
}

/**
 * Parse text containing link syntax [text](url) or [text](url){external}
 * Returns an array of text segments with links identified
 * 
 * Supported formats:
 * - [Link Text](https://example.com) - External link
 * - [Link Text](/internal/path) - Internal link
 * - [Link Text](https://example.com){external} - Explicitly mark as external
 * - [Link Text](/path){internal} - Explicitly mark as internal
 * 
 * @param text - The text to parse
 * @returns Array of text segments
 */
export function parseTextWithLinks(text: string): TextSegment[] {
  if (!text) return [{ type: 'text', content: '', url: undefined }];

  const segments: TextSegment[] = [];
  
  // Regex to match [text](url) or [text](url){external|internal}
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)(?:\{(external|internal)\})?/g;
  
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index);
      if (textBefore) {
        segments.push({ type: 'text', content: textBefore });
      }
    }

    // Extract link parts
    const linkText = match[1];
    const linkUrl = match[2];
    const linkType = match[3]; // 'external' or 'internal' or undefined

    // Determine if link is external
    let isExternal = false;
    if (linkType === 'external') {
      isExternal = true;
    } else if (linkType === 'internal') {
      isExternal = false;
    } else {
      // Auto-detect: if URL starts with http:// or https://, it's external
      isExternal = /^https?:\/\//i.test(linkUrl);
    }

    segments.push({
      type: 'link',
      content: linkText,
      url: linkUrl,
      external: isExternal
    });

    lastIndex = linkRegex.lastIndex;
  }

  // Add remaining text after the last link
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex);
    if (textAfter) {
      segments.push({ type: 'text', content: textAfter });
    }
  }

  // If no links found, return the original text
  if (segments.length === 0) {
    segments.push({ type: 'text', content: text });
  }

  return segments;
}

/**
 * Check if a text string contains any links
 * @param text - The text to check
 * @returns True if text contains links
 */
export function hasLinks(text: string): boolean {
  if (!text) return false;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/;
  return linkRegex.test(text);
}
